import { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Clock, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PreOrderModal = ({ open, onOpenChange, product, defaultCustomer = null }) => {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    delivery_address: '',
    notes: '',
  });

  useEffect(() => {
    if (!open) {
      // Reset on close after a short delay so the success screen isn't visible while closing
      const t = setTimeout(() => setSubmitted(false), 300);
      return () => clearTimeout(t);
    }
    if (defaultCustomer) {
      setForm((f) => ({
        ...f,
        customer_name: defaultCustomer.name || defaultCustomer.customer_name || f.customer_name,
        customer_email: defaultCustomer.email || defaultCustomer.customer_email || f.customer_email,
        customer_phone: defaultCustomer.phone || defaultCustomer.customer_phone || f.customer_phone,
      }));
    }
  }, [open, defaultCustomer]);

  const isValid =
    form.customer_name.trim().length >= 2 &&
    /^\S+@\S+\.\S+$/.test(form.customer_email) &&
    form.customer_phone.trim().length >= 6;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid || !product) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/orders`, {
        customer_name: form.customer_name.trim(),
        customer_email: form.customer_email.trim(),
        customer_phone: form.customer_phone.trim(),
        delivery_address: form.delivery_address.trim() || 'To be provided when stock arrives',
        items: [
          {
            product_id: product.id,
            product_name: product.name,
            quantity: 1,
            price: 0,
          },
        ],
        payment_method: 'pre_order',
      });
      setSubmitted(true);
      toast.success('Pre-order submitted! We will contact you when stock is available.');
    } catch (err) {
      console.error('Pre-order submit failed', err);
      toast.error(err?.response?.data?.detail || 'Could not submit pre-order. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="pre-order-modal">
        {submitted ? (
          <div className="text-center py-4">
            <div className="w-14 h-14 rounded-full bg-green-100 mx-auto mb-4 flex items-center justify-center">
              <Check className="w-7 h-7 text-green-600" />
            </div>
            <DialogTitle className="text-xl">Pre-Order Received!</DialogTitle>
            <p className="text-sm text-slate-500 mt-2">
              Thanks for pre-ordering <span className="font-medium">{product?.name}</span>. Our team
              will reach out to <span className="font-medium">{form.customer_email}</span> as soon as
              stock is available.
            </p>
            <Button
              className="mt-6 bg-[#0047AB] hover:bg-[#003380]"
              onClick={() => onOpenChange(false)}
              data-testid="pre-order-done-btn"
            >
              Done
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#0047AB]" />
                <DialogTitle>Pre-Order {product?.name}</DialogTitle>
              </div>
              <p className="text-xs text-slate-500 pt-1">
                No payment now. We&apos;ll notify you as soon as stock arrives and confirm pricing before dispatch.
              </p>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-3 mt-2">
              <div>
                <Label htmlFor="pre-order-name">Full name</Label>
                <Input
                  id="pre-order-name"
                  name="customer_name"
                  value={form.customer_name}
                  onChange={handleChange}
                  required
                  data-testid="pre-order-name-input"
                />
              </div>
              <div>
                <Label htmlFor="pre-order-email">Email</Label>
                <Input
                  id="pre-order-email"
                  name="customer_email"
                  type="email"
                  value={form.customer_email}
                  onChange={handleChange}
                  required
                  data-testid="pre-order-email-input"
                />
              </div>
              <div>
                <Label htmlFor="pre-order-phone">Phone (with country code)</Label>
                <Input
                  id="pre-order-phone"
                  name="customer_phone"
                  value={form.customer_phone}
                  onChange={handleChange}
                  placeholder="+91 9876543210"
                  required
                  data-testid="pre-order-phone-input"
                />
              </div>
              <div>
                <Label htmlFor="pre-order-notes">City / Notes (optional)</Label>
                <Textarea
                  id="pre-order-notes"
                  name="delivery_address"
                  value={form.delivery_address}
                  onChange={handleChange}
                  placeholder="City, or any note you want to add"
                  rows={2}
                  data-testid="pre-order-notes-input"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!isValid || submitting}
                  className="bg-[#0047AB] hover:bg-[#003380] text-white"
                  data-testid="pre-order-submit-btn"
                >
                  {submitting ? 'Submitting…' : 'Submit Pre-Order'}
                </Button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PreOrderModal;
